import type { KAPLAYCtx } from "kaplay";
import type { LocalGameState } from "../client/model/LocalGameState";

export interface FieldLayout {
  x: number;
  y: number;
  cellW: number;
  cellH: number;
}

export const FIELD_PADDING = 10;
const MIN_CELL_SIZE = 28;

export function computeLayout(
  canvasW: number,
  _canvasH: number,
  state: LocalGameState,
  panX = 0,
  panY = 0,
): FieldLayout {
  const fitCellW = (canvasW - FIELD_PADDING * 2) / state.fieldWidth;
  const cellSize = Math.max(fitCellW, MIN_CELL_SIZE);
  return { x: FIELD_PADDING + panX, y: FIELD_PADDING + panY, cellW: cellSize, cellH: cellSize };
}

export function fieldToCanvas(
  gx: number,
  gy: number,
  layout: FieldLayout,
  state: LocalGameState,
): { x: number; y: number } {
  return {
    x: layout.x + gx * layout.cellW,
    y: layout.y + (state.fieldHeight - 1 - gy) * layout.cellH,
  };
}

export function canvasToField(
  cx: number,
  cy: number,
  layout: FieldLayout,
  state: LocalGameState,
): { x: number; y: number } | null {
  const relX = cx - layout.x;
  const relY = cy - layout.y;
  const totalW = layout.cellW * state.fieldWidth;
  const totalH = layout.cellH * state.fieldHeight;
  if (relX < 0 || relY < 0 || relX > totalW || relY > totalH) return null;
  return {
    x: Math.floor(relX / layout.cellW),
    y: state.fieldHeight - 1 - Math.floor(relY / layout.cellH),
  };
}

export interface RenderOptions {
  highlights?: Set<string>;
  moveHighlights?: Set<string>;
  tackleHighlights?: Set<string>;
  throwHighlights?: Set<string>;
  placementPreview?: Map<string, [number, number]>;
  movePath?: Array<[number, number]>;
  movePathOrigin?: [number, number] | null;
  previewLine?: { from: [number, number]; to: [number, number] } | null;
  animatedPositions?: Map<string, [number, number]>;
  animationPaths?: Map<string, Array<[number, number]>>;
  myTeamIsHome?: boolean;
}

export function renderField(
  k: KAPLAYCtx,
  state: LocalGameState,
  layout: FieldLayout,
  opts: RenderOptions = {},
): void {
  const { x: ox, y: oy, cellW, cellH } = layout;
  const { fieldWidth: W, fieldHeight: H, endZoneDepth: ezd, outOfBoundsDepth: oob } = state;
  const { highlights = new Set(), moveHighlights = new Set(), tackleHighlights = new Set(), throwHighlights = new Set(), placementPreview = new Map(), movePath = [], movePathOrigin, previewLine, animatedPositions, animationPaths, myTeamIsHome } = opts;
  const totalW = cellW * W;

  // ─── Cells ────────────────────────────────────────────────────────────────
  for (let gy = 0; gy < H; gy++) {
    for (let gx = 0; gx < W; gx++) {
      const px = ox + gx * cellW;
      const py = oy + (H - 1 - gy) * cellH;
      const isOob = gx < oob || gx >= W - oob;
      const isHomeEz = gy < ezd;
      const isAwayEz = gy >= H - ezd;
      let r: number, g: number, b: number;
      if (isOob) { r = 10; g = 10; b = 18; }
      else if (isHomeEz) { r = 18; g = 28; b = 55; }
      else if (isAwayEz) { r = 55; g = 18; b = 18; }
      else { r = 22; g = 22; b = 42; }
      k.drawRect({ pos: k.vec2(px + 1, py + 1), width: cellW - 2, height: cellH - 2, color: k.rgb(r, g, b) });
    }
  }

  // ─── Move range highlight ─────────────────────────────────────────────────
  for (const key of moveHighlights) {
    const [gx, gy] = key.split(",").map(Number) as [number, number];
    const px = ox + gx * cellW;
    const py = oy + (H - 1 - gy) * cellH;
    k.drawRect({ pos: k.vec2(px + 1, py + 1), width: cellW - 2, height: cellH - 2, color: k.rgb(30, 80, 160), opacity: 0.55 });
  }

  // ─── Throw target highlight (cell keys "x,y") ─────────────────────────────
  for (const key of throwHighlights) {
    const [gx, gy] = key.split(",").map(Number) as [number, number];
    const px = ox + gx * cellW;
    const py = oy + (H - 1 - gy) * cellH;
    k.drawRect({ pos: k.vec2(px + 1, py + 1), width: cellW - 2, height: cellH - 2, color: k.rgb(40, 160, 80), opacity: 0.4 });
  }

  // ─── Line of scrimmage & first down line ─────────────────────────────────
  if (state.lineOfScrimmage !== null && state.firstDownLine !== null) {
    const edge = state.firstDownLine > state.lineOfScrimmage ? 1 : 0;
    const losY = oy + (H - state.lineOfScrimmage - edge) * cellH;
    k.drawLine({ p1: k.vec2(ox, losY), p2: k.vec2(ox + totalW, losY), color: k.rgb(255, 215, 50), width: 2 });
    const fdl = state.firstDownLine;
    const inEndZone = fdl < ezd || fdl >= H - ezd;
    if (!inEndZone) {
      const fdlY = oy + (H - fdl - edge) * cellH;
      k.drawLine({ p1: k.vec2(ox, fdlY), p2: k.vec2(ox + totalW, fdlY), color: k.rgb(50, 200, 90), width: 2 });
    }
  } else if (state.lineOfScrimmage !== null) {
    const losY = oy + (H - state.lineOfScrimmage) * cellH;
    k.drawLine({ p1: k.vec2(ox, losY), p2: k.vec2(ox + totalW, losY), color: k.rgb(255, 215, 50), width: 2 });
  }

  // ─── Move path (planned, local player) ───────────────────────────────────
  if (movePath.length > 0) {
    if (movePathOrigin) {
      const o = fieldToCanvas(movePathOrigin[0], movePathOrigin[1], layout, state);
      const f = fieldToCanvas(movePath[0][0], movePath[0][1], layout, state);
      k.drawLine({
        p1: k.vec2(o.x + cellW / 2, o.y + cellH / 2),
        p2: k.vec2(f.x + cellW / 2, f.y + cellH / 2),
        color: k.rgb(100, 190, 255),
        width: 3,
      });
    }
    for (let i = 0; i < movePath.length; i++) {
      const [gx, gy] = movePath[i];
      const c = fieldToCanvas(gx, gy, layout, state);
      const cx = c.x + cellW / 2;
      const cy = c.y + cellH / 2;
      if (i > 0) {
        const [pgx, pgy] = movePath[i - 1];
        const p = fieldToCanvas(pgx, pgy, layout, state);
        k.drawLine({
          p1: k.vec2(p.x + cellW / 2, p.y + cellH / 2),
          p2: k.vec2(cx, cy),
          color: k.rgb(100, 190, 255),
          width: 3,
        });
      }
      k.drawCircle({ pos: k.vec2(cx, cy), radius: 5, color: k.rgb(100, 190, 255) });
    }
  }

  // ─── Animation paths (opponent / server-confirmed movement) ──────────────
  if (animationPaths) {
    for (const [, path] of animationPaths) {
      if (path.length < 2) continue;
      for (let i = 1; i < path.length; i++) {
        const [pgx, pgy] = path[i - 1];
        const [gx, gy] = path[i];
        const p = fieldToCanvas(pgx, pgy, layout, state);
        const c = fieldToCanvas(gx, gy, layout, state);
        k.drawLine({
          p1: k.vec2(p.x + cellW / 2, p.y + cellH / 2),
          p2: k.vec2(c.x + cellW / 2, c.y + cellH / 2),
          color: k.rgb(100, 190, 255),
          width: 2,
        });
      }
      for (let i = 1; i < path.length - 1; i++) {
        const [gx, gy] = path[i];
        const c = fieldToCanvas(gx, gy, layout, state);
        k.drawCircle({ pos: k.vec2(c.x + cellW / 2, c.y + cellH / 2), radius: 4, color: k.rgb(100, 190, 255) });
      }
    }
  }

  // ─── Formation placement preview ──────────────────────────────────────────
  for (const [, [gx, gy]] of placementPreview) {
    const c = fieldToCanvas(gx, gy, layout, state);
    k.drawRect({
      pos: k.vec2(c.x + 2, c.y + 2),
      width: cellW - 4,
      height: cellH - 4,
      color: k.rgb(40, 140, 70),
    });
  }

  // ─── Ball (when no carrier) ───────────────────────────────────────────────
  if (!state.ballCarrierId && state.ballPosition) {
    const [bx, by] = state.ballPosition;
    const c = fieldToCanvas(bx, by, layout, state);
    k.drawCircle({
      pos: k.vec2(c.x + cellW / 2, c.y + cellH / 2),
      radius: Math.min(cellW, cellH) * 0.2,
      color: k.rgb(255, 170, 0),
    });
  }

  // ─── Lock-up connections ──────────────────────────────────────────────────
  for (const [idA, idB] of state.lockedPairs) {
    const posA = state.playerPositions.get(idA);
    const posB = state.playerPositions.get(idB);
    if (!posA || !posB) continue;
    const a = fieldToCanvas(posA[0], posA[1], layout, state);
    const b = fieldToCanvas(posB[0], posB[1], layout, state);
    k.drawLine({
      p1: k.vec2(a.x + cellW / 2, a.y + cellH / 2),
      p2: k.vec2(b.x + cellW / 2, b.y + cellH / 2),
      color: k.rgb(160, 80, 255),
      width: 3,
    });
  }

  // ─── Action preview line (tackle / throw) ────────────────────────────────
  if (previewLine) {
    const pf = fieldToCanvas(previewLine.from[0], previewLine.from[1], layout, state);
    const pt = fieldToCanvas(previewLine.to[0], previewLine.to[1], layout, state);
    k.drawLine({
      p1: k.vec2(pf.x + cellW / 2, pf.y + cellH / 2),
      p2: k.vec2(pt.x + cellW / 2, pt.y + cellH / 2),
      color: k.rgb(255, 220, 60),
      width: 3,
      opacity: 0.85,
    });
  }

  // ─── Players ──────────────────────────────────────────────────────────────
  const allPositions = new Map<string, [number, number]>([...state.playerPositions, ...placementPreview]);
  if (animatedPositions) {
    for (const [pid, pos] of animatedPositions) allPositions.set(pid, pos);
  }

  for (const [pid, [gx, gy]] of allPositions) {
    const c = fieldToCanvas(gx, gy, layout, state);
    const cx = c.x + cellW / 2;
    const cy = c.y + cellH / 2;
    const isHome = state.isHomePlayer(pid);
    const isMine = myTeamIsHome !== undefined ? (isHome === myTeamIsHome) : isHome;
    const status = state.getPlayerStatus(pid);
    const isCarrier = state.ballCarrierId === pid;
    const isHL = highlights.has(pid);
    const isLocked = state.lockedGroups.some((g) => g.has(pid));
    const isActivated = state.activatedThisRound.has(pid);
    const label = state.getName(pid);
    const r = Math.min(cellW, cellH) * 0.4;

    // Rings
    if (isHL) k.drawCircle({ pos: k.vec2(cx, cy), radius: r + 5, color: k.rgb(255, 200, 50) });
    if (tackleHighlights.has(pid)) k.drawCircle({ pos: k.vec2(cx, cy), radius: r + 5, color: k.rgb(220, 60, 60) });
    if (throwHighlights.has(`${gx},${gy}`)) k.drawCircle({ pos: k.vec2(cx, cy), radius: r + 5, color: k.rgb(60, 200, 100) });
    if (isLocked) k.drawCircle({ pos: k.vec2(cx, cy), radius: r + 3, color: k.rgb(160, 80, 255) });

    const isProne = status === "PRONE" || status === "STUNNED" || status === "KNOCKED_OUT" || status === "INCAPACITATED";
    if (status === "PRONE")          k.drawCircle({ pos: k.vec2(cx, cy), radius: r + 2, color: k.rgb(255, 215, 50) });
    else if (status === "STUNNED")   k.drawCircle({ pos: k.vec2(cx, cy), radius: r + 2, color: k.rgb(255, 140, 0) });
    else if (status === "KNOCKED_OUT")    k.drawCircle({ pos: k.vec2(cx, cy), radius: r + 2, color: k.rgb(210, 45, 45) });
    else if (status === "INCAPACITATED")  k.drawCircle({ pos: k.vec2(cx, cy), radius: r + 2, color: k.rgb(160, 0, 180) });

    const mix = status === "PRONE" ? 0.35 : status === "STUNNED" ? 0.45 : status === "KNOCKED_OUT" ? 0.55 : status === "INCAPACITATED" ? 0.65 : 0;
    const baseR = isMine ? 50 : 210;
    const baseG = isMine ? 110 : 100;
    const baseB = isMine ? 205 : 25;
    const fr = Math.round(baseR + (128 - baseR) * mix);
    const fg = Math.round(baseG + (128 - baseG) * mix);
    const fb = Math.round(baseB + (128 - baseB) * mix);
    k.drawCircle({ pos: k.vec2(cx, cy), radius: r, color: k.rgb(fr, fg, fb) });

    if (isActivated && !isProne) {
      k.drawCircle({ pos: k.vec2(cx, cy), radius: r - 2, color: k.rgb(fr - 20, fg - 20, fb - 20) });
    }

    if (isCarrier) {
      k.drawCircle({ pos: k.vec2(cx - r * 0.4, cy - r * 0.4), radius: r * 0.28, color: k.rgb(255, 170, 0) });
    }

    k.drawText({
      text: label,
      pos: k.vec2(cx, cy),
      size: Math.max(8, Math.min(13, cellH * 0.4)),
      color: k.rgb(235, 235, 235),
      anchor: "center",
    });
  }
}
